import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Divider, Button, Stack, Chip, alpha } from "@mui/material";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import HotelIcon from "@mui/icons-material/Hotel";
import { useNavigate } from "react-router-dom";

const responsive = {
  desktop: { breakpoint: { max: 3000, min: 1024 }, items: 1 },
  tablet: { breakpoint: { max: 1024, min: 464 }, items: 1 },
  mobile: { breakpoint: { max: 464, min: 0 }, items: 1 },
};

// 🌐 Origen de la API (dev + prod)
const API_ORIGIN =
  process.env.REACT_APP_API_ORIGIN ||
  (typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:4000"
    : "https://api.ubika.site");

const resolveImageUrl = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== "string") return null;
  const url = rawUrl.trim();
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/")) return `${API_ORIGIN}${url}`;
  return `${API_ORIGIN}/${url}`;
};

const getRoomImages = (room) => {
  let rawList = [];
  if (Array.isArray(room.images) && room.images.length) rawList = room.images;
  else if (Array.isArray(room.imageUrls) && room.imageUrls.length) rawList = room.imageUrls;
  else if (typeof room.imageUrl === "string" && room.imageUrl.trim()) rawList = [room.imageUrl.trim()];

  const resolved = rawList
    .map((item) => {
      if (typeof item === "string") return resolveImageUrl(item);
      if (item && typeof item === "object") return resolveImageUrl(item.url || item.path || item.src);
      return null;
    })
    .filter(Boolean);

  return resolved.length ? resolved : ["https://via.placeholder.com/800x600?text=Sin+imagen+disponible"];
};

// 🎨 Paleta “Bichón”
const bichon = {
  bg: "#F7F3EC",
  paper: "#FFFFFF",
  text: "#102A2E",
  muted: "#5E6B6E",
  primary: "#0F6E6A",
  primaryDark: "#0A5451",
  border: "#D9D2C7",
  success: "#1F8A4C",
  danger: "#D92D20",
};

const HabitacionCard = ({
  room,
  formatCurrency,
  calculateOffer,
  startDate,
  endDate,
  numGuests,
  currency,
  propertyId,
  mpPublicKey,
  mpLocale,
}) => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [availableUnits, setAvailableUnits] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const avail = Array.isArray(room.availability) ? room.availability : [];
    setIsAvailable(avail.length > 0);
    setAvailableUnits(avail);
  }, [room.availability, room]);

  const roomCurrency = (room.currency || currency || "ARS").toUpperCase();

  const totalNights = Math.max(
    (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24),
    1
  );

  const totalOffer = useMemo(() => calculateOffer(room), [calculateOffer, room]);
  const roomImages = useMemo(() => getRoomImages(room), [room]);
  const amenities = Array.isArray(room.amenities) ? room.amenities : [];

  const isReservable =
    isAvailable &&
    ((room.type === "shared" && availableUnits.length >= numGuests) ||
      (room.type === "private" && numGuests <= room.maxGuests));

  const handleReservar = () => {
    if (room.type === "shared") {
      if (!Array.isArray(room.availability) || room.availability.length < numGuests) {
        alert("No hay suficientes camas disponibles para la cantidad de huéspedes.");
        return;
      }
    }

    const selectedUnitId =
      room.type === "private" && Array.isArray(room.availability) && room.availability.length
        ? room.availability[0]
        : null;

    const selectedUnitIds =
      room.type === "shared" ? room.availability.slice(0, numGuests) : [selectedUnitId];

    navigate("/pay", {
      state: {
        roomId: room.roomId,
        name: room.name,
        unitIds: selectedUnitIds,
        type: room.type,
        pricePerGuest: room.pricePerGuest,
        totalOffer,
        startDate,
        endDate,
        numGuests,
        totalNights,
        currency: roomCurrency,
        propertyId,
        mpPublicKey,
        mpLocale,
      },
    });
  };

  const statusChip = isAvailable
    ? { label: "Disponible", bg: alpha(bichon.success, 0.12), color: bichon.success, border: alpha(bichon.success, 0.35) }
    : { label: "Ocupada", bg: alpha(bichon.danger, 0.12), color: bichon.danger, border: alpha(bichon.danger, 0.35) };

  return (
    <Box
      sx={{
        borderRadius: 3,
        overflow: "hidden",
        background: bichon.paper,
        boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
        border: `1px solid ${bichon.border}`,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
      }}
    >
      {/* 🖼️ Media */}
      <Box
        sx={{
          p: { xs: 1.25, sm: 1.75 },
          background: bichon.bg,
          borderRight: { lg: `1px solid ${bichon.border}` },
        }}
      >
        {/* ✅ Frame 4:3 REAL (no aspectRatio) */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            pt: "75%", // 4:3
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 10px 24px rgba(0,0,0,0.10)",
            backgroundColor: "#000",

            // ✅ Forzamos el carousel a llenar el frame
            "& .react-multi-carousel-list": { position: "absolute", inset: 0, height: "100%" },
            "& .react-multi-carousel-track": { height: "100%", alignItems: "stretch" },
            "& .react-multi-carousel-item": { height: "100%", display: "flex" },

            // ✅ Mata cualquier zoom/transform que se cuele (de lib o CSS global)
            "& img": {
              transform: "none !important",
              transition: "none !important",
              filter: "none !important",
            },
          }}
        >
          <Carousel
            responsive={responsive}
            infinite
            autoPlay
            autoPlaySpeed={3500}
            arrows
            showDots
            keyBoardControl
            draggable
            swipeable
            containerClass="bichon-carousel"
            itemClass="bichon-carousel-item"
          >
            {roomImages.map((image, index) => (
              <Box
                key={image + index}
                sx={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  overflow: "hidden",
                  backgroundColor: "#000",
                  flex: 1,
                }}
              >
                {/* Fondo blur (no afecta al contenido, está atrás) */}
                <Box
                  aria-hidden
                  sx={{
                    position: "absolute",
                    inset: 0,
                    backgroundImage: `url(${image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    filter: "blur(14px) saturate(1.05)",
                    transform: "scale(1.12)",
                    opacity: 0.55,
                  }}
                />

                {/* Imagen principal: estable, sin recorte, sin zoom */}
                <Box
                  component="img"
                  src={image}
                  alt={`Habitación ${room.name} - foto ${index + 1}`}
                  loading="lazy"
                  draggable={false}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    margin: "auto",
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    objectPosition: "center",
                    display: "block",
                    transform: "none !important",
                    transition: "none !important",
                  }}
                />
              </Box>
            ))}
          </Carousel>
        </Box>
      </Box>

      {/* 🧾 Info */}
      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              color: bichon.text,
              fontWeight: 800,
              lineHeight: 1.1,
              textTransform: "capitalize",
              mb: 1,
              fontSize: { xs: "1.2rem", sm: "1.35rem" },
            }}
          >
            {room.name}{" "}
            <Box component="span" sx={{ color: bichon.muted, fontWeight: 600 }}>
              · {room.type === "shared" ? "Compartida" : "Privada"}
            </Box>
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
            <Chip
              icon={<HotelIcon />}
              label={`Máx. ${room.maxGuests} ${room.maxGuests > 1 ? "personas" : "persona"}`}
              variant="outlined"
              sx={{
                borderRadius: 999,
                borderColor: alpha(bichon.primary, 0.35),
                color: bichon.primary,
                "& .MuiSvgIcon-root": { color: bichon.primary },
                background: alpha(bichon.primary, 0.06),
              }}
            />
            <Chip
              label={statusChip.label}
              sx={{
                borderRadius: 999,
                background: statusChip.bg,
                color: statusChip.color,
                border: `1px solid ${statusChip.border}`,
                fontWeight: 700,
              }}
            />
          </Stack>

          <Divider sx={{ mb: 1.5 }} />

          <Typography
            variant="body2"
            sx={{ color: bichon.danger, textDecoration: "line-through", mb: 0.5 }}
          >
            Precio por {room.type === "private" ? "noche" : "huésped"}:{" "}
            <Box component="span" sx={{ fontWeight: 800 }}>
              {formatCurrency(room.pricePerGuest * 1.25)} {roomCurrency}
            </Box>
          </Typography>

          <Typography variant="body2" sx={{ mb: 1 }}>
            20% OFF. Precio por {room.type === "private" ? "noche" : "huésped"}:{" "}
            <Box component="span" sx={{ fontWeight: 800, color: bichon.text }}>
              {formatCurrency(room.pricePerGuest)} {roomCurrency}
            </Box>
          </Typography>

          <Box
            sx={{
              borderRadius: 2.5,
              p: 1.25,
              background: alpha(bichon.primary, 0.08),
              border: `1px solid ${alpha(bichon.primary, 0.18)}`,
              mb: 1.5,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                color: bichon.primaryDark,
                fontWeight: 900,
                fontSize: { xs: "1.05rem", sm: "1.15rem" },
              }}
            >
              Total por {totalNights} noche{totalNights > 1 ? "s" : ""}:{" "}
              {formatCurrency(totalOffer)} {roomCurrency}
            </Typography>
          </Box>

          {amenities.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ color: bichon.text, mb: 0.75, fontWeight: 800 }}>
                Servicios incluidos:
              </Typography>

              <Stack spacing={0.35} sx={{ ml: 1 }}>
                {amenities.slice(0, 7).map((am, idx) => (
                  <Typography key={idx} variant="body2" sx={{ color: bichon.muted }}>
                    • {am}
                  </Typography>
                ))}
                {amenities.length > 7 && (
                  <Typography variant="body2" sx={{ color: bichon.muted }}>
                    • y {amenities.length - 7} más…
                  </Typography>
                )}
              </Stack>
            </>
          )}

          {room.type === "shared" && (
            <Typography variant="body2" sx={{ mt: 1, color: bichon.muted }}>
              🛏️ {availableUnits.length} camas disponibles
            </Typography>
          )}
        </Box>

        <Button
          onClick={handleReservar}
          disabled={!isReservable}
          fullWidth
          sx={{
            borderRadius: 2.5,
            py: 1.25,
            fontWeight: 900,
            textTransform: "none",
            backgroundColor: bichon.primary,
            color: "#fff",
            boxShadow: "0 10px 24px rgba(15,110,106,0.28)",
            "&:hover": { backgroundColor: bichon.primaryDark },
            opacity: isReservable ? 1 : 0.45,
          }}
        >
          {!isAvailable
            ? "Ocupada"
            : room.type === "shared" && availableUnits.length < numGuests
            ? "Camas insuficientes"
            : room.type === "private" && numGuests > room.maxGuests
            ? "Excede capacidad"
            : "Reservar ahora"}
        </Button>
      </Box>
    </Box>
  );
};

export default HabitacionCard;
